/** Session state: who is signed in, and the mutations that change that. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useMemo, type ReactNode } from "react";

import { ApiError, api, type Me, type User } from "../../lib/api";

/** What `POST /auth/register` answers. No user, because none has been created yet. */
export interface RegistrationStarted {
  email: string;
  verification_required: boolean;
  email_delivery: boolean;
}

interface SessionValue {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, name: string, password: string) => Promise<RegistrationStarted>;
  logout: () => Promise<void>;
  /** Re-read `/auth/me`, e.g. after confirming an email in another tab. */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Drop every service-worker cache holding this person's data.
 *
 * `queryClient.clear()` only empties React Query's in-memory cache, which dies with the
 * tab anyway. The service worker's Cache Storage is the one that outlives sign-out: it
 * is keyed by origin, not by session, so on a shared or borrowed device the ledger and
 * balances of whoever was signed in before stayed readable — and the PWA would paint
 * views from them before the 401 arrived.
 *
 * The precached app shell is left alone: it is the same static bundle for everybody, and
 * throwing it away only means a slower next start.
 */
async function clearOfflineCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((name) => name.startsWith("splitwell-"))
        .map((name) => caches.delete(name)),
    );
  } catch (error) {
    // Private-browsing modes can refuse this outright. Signing out must still work.
    console.warn("could not clear the offline cache", error);
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<Me>("/auth/me");
      } catch (error) {
        // Not signed in is a normal state, not a failure to retry.
        if (error instanceof ApiError && error.isAuth) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<Me>("/auth/login", input),
    onSuccess: (me) => {
      queryClient.setQueryData(["me"], me);
      void queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (input: { email: string; name: string; password: string }) =>
      api.post<RegistrationStarted>("/auth/register", input),
    onSuccess: (started) => {
      // Registering no longer signs anyone in — there is no account until the emailed
      // link is followed. The only case that does is an install with verification
      // switched off, where the server has already set the cookies.
      if (!started.verification_required) {
        void queryClient.invalidateQueries({ queryKey: ["me"] });
        void queryClient.invalidateQueries({ queryKey: ["groups"] });
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<{ ok: boolean }>("/auth/logout"),
    onSuccess: async () => {
      queryClient.setQueryData(["me"], null);
      queryClient.clear();
      await clearOfflineCaches();
    },
  });

  const value = useMemo<SessionValue>(
    () => ({
      user: data?.user ?? null,
      isLoading,
      login: async (email, password) => {
        const me = await loginMutation.mutateAsync({ email, password });
        return me.user;
      },
      register: (email, name, password) =>
        registerMutation.mutateAsync({ email, name, password }),
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
      },
    }),
    [data, isLoading, loginMutation, registerMutation, logoutMutation, queryClient],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}
